import { makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { ClientTaxRateConfig, ObligationCountyAllocation } from '../../types';
import {
  describeSurtax,
  formatRatePercent,
  formatAllocationSummary,
} from '../../utils/surtaxDisplay';

/**
 * The surtax an obligation's filings actually compute at, on the obligation itself.
 *
 * 🔴 Measured on 45f15de, this was invisible: `ClientTaxRateConfigDialog` was mounted in exactly
 * ONE place — `ComplianceTaskDetailPage`, a TASK page — so staff had to open a task to see the
 * rate, and neither `ObligationsCard` nor `ClientObligationsPage` showed it at all.
 *
 * Deliberately a plain inline element rather than a Fluent Popover or Tooltip. It renders inside
 * table rows on two pages, and this repo's open B3 flake is specifically about Fluent surfaces
 * portalling onto `document.body` in jsdom. There is nothing here to portal.
 */

export interface SurtaxLineProps {
  allocation: ObligationCountyAllocation | null | undefined;
  blendedSurtaxRate: number | null | undefined;
  rateConfig: (ClientTaxRateConfig & { effectiveSurtaxRate?: number | null }) | null | undefined;
}

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
  },
  rate: { fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  warn: { color: tokens.colorPaletteRedForeground1 },
  // The manual-override marker: staff need to tell "what Florida publishes" from "what somebody
  // typed", because only one of those is worth a second look.
  flagged: { color: tokens.colorPaletteDarkOrangeForeground1 },
});

export default function SurtaxLine({ allocation, blendedSurtaxRate, rateConfig }: SurtaxLineProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const d = describeSurtax(allocation, blendedSurtaxRate, rateConfig);

  const stateRate = rateConfig?.stateRate ?? 0.06;
  // The COMBINED rate is what the math actually uses, so it is shown alongside — a staff member
  // reconciling a filing against a POS report is comparing against the combined figure.
  const combined = d.rate === null ? null : stateRate + d.rate;

  return (
    <span className={styles.root} data-testid="surtax-line" data-provenance={d.provenance}>
      <span className={styles.muted}>{t('surtaxLine.label')}</span>
      <span className={d.provenance === 'unset' ? styles.warn : styles.rate}>
        {formatRatePercent(d.rate)}
      </span>

      {d.isAllocated && d.counties.length > 1 ? (
        <>
          {/* ⚠ "allocated", never "measured". A fixed share applied to a period's aggregate
              taxable sales is an ESTIMATE of where those sales happened — the same family as the
              $5,000-per-item FL surtax cap the engine cannot apply. Staff reconcile against it. */}
          <span className={styles.muted}>
            {t('surtaxLine.allocatedAcross', { count: d.counties.length })}
          </span>
          <span className={styles.muted}>{formatAllocationSummary(d.counties)}</span>
        </>
      ) : null}

      {d.county && (!d.isAllocated || d.counties.length === 1) ? (
        <span className={styles.muted}>
          {'·'} {d.county}
          {d.isAllocated ? ` ${t('surtaxLine.allocatedSingle')}` : ''}
        </span>
      ) : null}

      {d.provenance === 'override' ? (
        <span className={styles.flagged}>{t('surtaxLine.override')}</span>
      ) : null}

      {combined !== null ? (
        <span className={styles.muted}>
          {t('surtaxLine.combined', { rate: formatRatePercent(combined) })}
        </span>
      ) : null}

      {d.provenance === 'unset' ? (
        // Today's silent compute-time hard stop, surfaced BEFORE it blocks a filing. The engine
        // already refuses to guess a rate; this is that refusal, said early and in the one place
        // a staff member can act on it.
        <span className={styles.warn}>
          {d.county
            ? t('surtaxLine.countyUnresolvable', { county: d.county })
            : t('surtaxLine.countyNotSet')}
        </span>
      ) : null}
    </span>
  );
}
